// SPDX-License-Identifier: AGPL-3.0-or-later

import type {CompanionRecording} from '@app/features/companion/commands/CompanionCommands';
import * as CompanionCommands from '@app/features/companion/commands/CompanionCommands';
import styles from '@app/features/companion/components/records/CompanionRecords.module.css';
import {Button} from '@app/features/ui/button/Button';
import {Slider} from '@app/features/ui/components/Slider';
import {formatDuration} from '@fluxer/date_utils/src/DateDuration';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {CircleNotchIcon, PauseIcon, PlayIcon, SpeakerHighIcon, SpeakerSlashIcon} from '@phosphor-icons/react';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

const LOAD_RECORDINGS_FAILED_DESCRIPTOR = msg({
	message: 'Failed to load recordings',
	comment: 'Error shown when companion recording audio cannot be loaded for synchronized playback.',
});
const PLAY_MIX_DESCRIPTOR = msg({
	message: 'Play',
	comment: 'Play button label on the multi-track companion recording player.',
});
const PAUSE_MIX_DESCRIPTOR = msg({
	message: 'Pause',
	comment: 'Pause button label on the multi-track companion recording player.',
});
const TIMELINE_DESCRIPTOR = msg({
	message: 'Playback timeline',
	comment: 'Accessible label for the multi-track companion recording timeline slider.',
});
const MUTE_TRACK_DESCRIPTOR = msg({
	message: 'Mute {speaker}',
	comment: 'Accessible label for muting one speaker track in multi-track playback. Preserve {speaker}.',
});
const UNMUTE_TRACK_DESCRIPTOR = msg({
	message: 'Unmute {speaker}',
	comment: 'Accessible label for unmuting one speaker track in multi-track playback. Preserve {speaker}.',
});

interface SpeakerTrackPlan {
	readonly speaker: string;
	readonly label: string;
	readonly startOffsetMs: number;
	readonly durationMs: number;
	readonly parts: ReadonlyArray<{
		readonly recording: CompanionRecording;
		readonly localStartMs: number;
		readonly localEndMs: number;
	}>;
}

export interface CompanionPlaybackSeekRequest {
	readonly timestampMs: number;
	readonly nonce: number;
}

interface CompanionMultiTrackPlayerProps {
	readonly sessionId: string;
	readonly recordings: ReadonlyArray<CompanionRecording>;
	readonly sessionStartedAt?: string;
	readonly sessionEndedAt?: string | null;
	readonly getSpeakerTrackLabel: (speaker: string) => string;
	readonly seekRequest?: CompanionPlaybackSeekRequest | null;
}

function buildTrackPlans(
	recordings: ReadonlyArray<CompanionRecording>,
	sessionStartedAt: string | undefined,
	sessionEndedAt: string | null | undefined,
	getSpeakerTrackLabel: (speaker: string) => string,
): {tracks: Array<SpeakerTrackPlan>; totalDurationMs: number} {
	const grouped = new Map<string, Array<CompanionRecording>>();
	for (const rec of recordings) {
		const list = grouped.get(rec.speaker) ?? [];
		list.push(rec);
		grouped.set(rec.speaker, list);
	}

	const sessionStartMs = sessionStartedAt ? Date.parse(sessionStartedAt) : Number.NaN;
	const sessionEndMs = sessionEndedAt ? Date.parse(sessionEndedAt) : Number.NaN;

	const rawTracks: Array<{
		speaker: string;
		label: string;
		rawOffsetMs: number;
		durationMs: number;
		parts: SpeakerTrackPlan['parts'];
	}> = [];

	for (const [speaker, list] of grouped.entries()) {
		const sorted = [...list].sort((a, b) => a.part - b.part);
		let cursorMs = 0;
		const parts = sorted.map((recording) => {
			const dur = Math.max(250, recording.duration_ms);
			const entry = {
				recording,
				localStartMs: cursorMs,
				localEndMs: cursorMs + dur,
			};
			cursorMs += dur;
			return entry;
		});
		const durationMs = cursorMs;
		const firstCreatedMs = sorted[0]?.created_at ? Date.parse(sorted[0].created_at) : Number.NaN;
		let rawOffsetMs = 0;
		if (Number.isFinite(sessionStartMs) && Number.isFinite(firstCreatedMs) && firstCreatedMs > sessionStartMs) {
			const candidateOffset = firstCreatedMs - sessionStartMs;
			// 历史旧分片的 created_at 写在会话结束收尾时刻(created_at + duration 远超 ended_at), 自动对齐回起点。
			if (Number.isFinite(sessionEndMs) && firstCreatedMs + durationMs > sessionEndMs + 3000) {
				rawOffsetMs = Math.max(0, sessionEndMs - sessionStartMs - durationMs);
			} else {
				rawOffsetMs = candidateOffset;
			}
		}
		rawTracks.push({
			speaker,
			label: getSpeakerTrackLabel(speaker),
			rawOffsetMs,
			durationMs,
			parts,
		});
	}

	const minOffsetMs = rawTracks.length > 0 ? Math.min(...rawTracks.map((t) => t.rawOffsetMs)) : 0;
	const tracks: Array<SpeakerTrackPlan> = rawTracks.map((t) => ({
		speaker: t.speaker,
		label: t.label,
		startOffsetMs: Math.max(0, t.rawOffsetMs - minOffsetMs),
		durationMs: t.durationMs,
		parts: t.parts,
	}));
	const totalDurationMs = Math.max(1000, ...tracks.map((t) => t.startOffsetMs + t.durationMs));
	return {tracks, totalDurationMs};
}

export function CompanionMultiTrackPlayer({
	sessionId,
	recordings,
	sessionStartedAt,
	sessionEndedAt,
	getSpeakerTrackLabel,
	seekRequest = null,
}: CompanionMultiTrackPlayerProps) {
	const {i18n} = useLingui();
	const {tracks, totalDurationMs} = useMemo(
		() => buildTrackPlans(recordings, sessionStartedAt, sessionEndedAt, getSpeakerTrackLabel),
		[getSpeakerTrackLabel, recordings, sessionEndedAt, sessionStartedAt],
	);

	const [isPlaying, setIsPlaying] = useState(false);
	const [isLoadingAudio, setIsLoadingAudio] = useState(false);
	const [positionMs, setPositionMs] = useState(0);
	const [mutedSpeakers, setMutedSpeakers] = useState<ReadonlySet<string>>(() => new Set());
	const [error, setError] = useState<string | null>(null);

	const audioElementsRef = useRef<Map<string, {audio: HTMLAudioElement; partId: string | null}>>(new Map());
	const blobUrlsRef = useRef<Map<string, string>>(new Map());
	const pendingLoadsRef = useRef<Map<string, Promise<string>>>(new Map());
	const anchorPerfRef = useRef<number>(0);
	const anchorPosMsRef = useRef<number>(0);
	const isPlayingRef = useRef(false);
	const mutedSpeakersRef = useRef<ReadonlySet<string>>(mutedSpeakers);
	const unmountedRef = useRef(false);

	mutedSpeakersRef.current = mutedSpeakers;

	const ensureRecordingUrl = useCallback(
		(recordingId: string): Promise<string> => {
			const cached = blobUrlsRef.current.get(recordingId);
			if (cached) return Promise.resolve(cached);
			const inflight = pendingLoadsRef.current.get(recordingId);
			if (inflight) return inflight;
			const promise = CompanionCommands.getRecordingAudio(sessionId, recordingId)
				.then((blob) => {
					const url = URL.createObjectURL(blob);
					if (unmountedRef.current) {
						URL.revokeObjectURL(url);
						throw new Error('Player unmounted');
					}
					blobUrlsRef.current.set(recordingId, url);
					pendingLoadsRef.current.delete(recordingId);
					return url;
				})
				.catch((loadError) => {
					pendingLoadsRef.current.delete(recordingId);
					if (!unmountedRef.current) {
						setError(
							CompanionCommands.getCompanionCommandErrorMessage(loadError) ?? i18n._(LOAD_RECORDINGS_FAILED_DESCRIPTOR),
						);
					}
					throw loadError;
				});
			pendingLoadsRef.current.set(recordingId, promise);
			return promise;
		},
		[i18n, sessionId],
	);

	const pauseAllElements = useCallback(() => {
		for (const entry of audioElementsRef.current.values()) {
			entry.audio.pause();
		}
	}, []);

	const syncTracksAt = useCallback(
		async (targetMs: number, shouldPlay: boolean) => {
			if (typeof Audio === 'undefined') return;
			const clampedMs = Math.max(0, Math.min(totalDurationMs, targetMs));
			for (const track of tracks) {
				let entry = audioElementsRef.current.get(track.speaker);
				if (!entry) {
					entry = {audio: new Audio(), partId: null};
					entry.audio.preload = 'auto';
					audioElementsRef.current.set(track.speaker, entry);
				}
				entry.audio.muted = mutedSpeakersRef.current.has(track.speaker);

				const localMs = clampedMs - track.startOffsetMs;
				if (localMs < 0 || localMs >= track.durationMs) {
					entry.audio.pause();
					continue;
				}
				const activePart = track.parts.find((p) => localMs >= p.localStartMs && localMs < p.localEndMs);
				if (!activePart) {
					entry.audio.pause();
					continue;
				}
				const url = await ensureRecordingUrl(activePart.recording.id);
				if (unmountedRef.current) return;
				if (entry.partId !== activePart.recording.id) {
					entry.audio.src = url;
					entry.partId = activePart.recording.id;
				}
				const desiredSec = Math.max(0, (localMs - activePart.localStartMs) / 1000);
				if (Math.abs(entry.audio.currentTime - desiredSec) > 0.35) {
					try {
						entry.audio.currentTime = desiredSec;
					} catch {
						/* Metadata may still be settling; next tick aligns currentTime. */
					}
				}
				if (shouldPlay && isPlayingRef.current) {
					if (entry.audio.paused) {
						await entry.audio.play().catch(() => {});
					}
				} else {
					entry.audio.pause();
				}
			}
		},
		[ensureRecordingUrl, totalDurationMs, tracks],
	);

	useEffect(() => {
		unmountedRef.current = false;
		return () => {
			unmountedRef.current = true;
			isPlayingRef.current = false;
			for (const entry of audioElementsRef.current.values()) {
				entry.audio.pause();
				entry.audio.removeAttribute('src');
			}
			audioElementsRef.current.clear();
			for (const url of blobUrlsRef.current.values()) {
				URL.revokeObjectURL(url);
			}
			blobUrlsRef.current.clear();
			pendingLoadsRef.current.clear();
		};
	}, []);

	useEffect(() => {
		if (!isPlaying) return;
		const timer = setInterval(() => {
			const elapsed = performance.now() - anchorPerfRef.current;
			const currentMs = anchorPosMsRef.current + elapsed;
			if (currentMs >= totalDurationMs) {
				isPlayingRef.current = false;
				setIsPlaying(false);
				setPositionMs(totalDurationMs);
				pauseAllElements();
				return;
			}
			setPositionMs(currentMs);
			void syncTracksAt(currentMs, true).catch(() => {});
		}, 200);
		return () => clearInterval(timer);
	}, [isPlaying, pauseAllElements, syncTracksAt, totalDurationMs]);

	const handleSeek = useCallback(
		(nextMs: number) => {
			const clamped = Math.max(0, Math.min(totalDurationMs, nextMs));
			anchorPosMsRef.current = clamped;
			anchorPerfRef.current = performance.now();
			setPositionMs(clamped);
			if (isPlayingRef.current) {
				void syncTracksAt(clamped, true).catch(() => {});
			} else {
				pauseAllElements();
			}
		},
		[pauseAllElements, syncTracksAt, totalDurationMs],
	);

	useEffect(() => {
		if (!seekRequest) return;
		handleSeek(seekRequest.timestampMs);
	}, [handleSeek, seekRequest]);

	const handleTogglePlay = useCallback(async () => {
		if (isPlayingRef.current) {
			const elapsed = performance.now() - anchorPerfRef.current;
			const pausedAt = Math.min(totalDurationMs, anchorPosMsRef.current + elapsed);
			isPlayingRef.current = false;
			setIsPlaying(false);
			anchorPosMsRef.current = pausedAt;
			setPositionMs(pausedAt);
			pauseAllElements();
			return;
		}
		setError(null);
		setIsLoadingAudio(true);
		try {
			await Promise.all(recordings.map((rec) => ensureRecordingUrl(rec.id)));
			if (unmountedRef.current) return;
			const resumeFrom = positionMs >= totalDurationMs - 200 ? 0 : positionMs;
			anchorPosMsRef.current = resumeFrom;
			anchorPerfRef.current = performance.now();
			setPositionMs(resumeFrom);
			isPlayingRef.current = true;
			setIsPlaying(true);
			await syncTracksAt(resumeFrom, true);
		} catch {
			isPlayingRef.current = false;
			setIsPlaying(false);
			pauseAllElements();
		} finally {
			if (!unmountedRef.current) setIsLoadingAudio(false);
		}
	}, [ensureRecordingUrl, pauseAllElements, positionMs, recordings, syncTracksAt, totalDurationMs]);

	const handleToggleMute = useCallback((speaker: string) => {
		setMutedSpeakers((prev) => {
			const next = new Set(prev);
			if (next.has(speaker)) next.delete(speaker);
			else next.add(speaker);
			const entry = audioElementsRef.current.get(speaker);
			if (entry) entry.audio.muted = next.has(speaker);
			return next;
		});
	}, []);

	return (
		<div className={styles.recordingPlayback} data-flx="companion.records.recording-playback.container">
			<div className={styles.multiTrackCard} data-flx="companion.records.multi-track.card">
				<div className={styles.multiTrackControls} data-flx="companion.records.multi-track.controls">
					<Button
						type="button"
						variant="primary"
						small
						fitContent
						disabled={isLoadingAudio}
						onClick={() => void handleTogglePlay()}
						data-flx="companion.records.multi-track.button.play-toggle"
					>
						{isLoadingAudio ? (
							<CircleNotchIcon
								size={16}
								className={styles.spinningIcon}
								aria-hidden="true"
								data-flx="companion.records.multi-track.loading-icon"
							/>
						) : isPlaying ? (
							<PauseIcon
								size={16}
								weight="fill"
								aria-hidden="true"
								data-flx="companion.records.multi-track.pause-icon"
							/>
						) : (
							<PlayIcon size={16} weight="fill" aria-hidden="true" data-flx="companion.records.multi-track.play-icon" />
						)}
						<span data-flx="companion.records.multi-track.play-label">
							{isPlaying ? i18n._(PAUSE_MIX_DESCRIPTOR) : i18n._(PLAY_MIX_DESCRIPTOR)}
						</span>
					</Button>
					<div className={styles.multiTrackSliderWrap} data-flx="companion.records.multi-track.slider-wrap">
						<Slider
							mini
							defaultValue={0}
							factoryDefaultValue={0}
							minValue={0}
							maxValue={totalDurationMs}
							step={200}
							value={Math.min(totalDurationMs, positionMs)}
							ariaLabel={i18n._(TIMELINE_DESCRIPTOR)}
							asValueChanges={handleSeek}
							onValueChange={handleSeek}
							onValueRender={(val) => formatDuration(Math.max(0, val) / 1000, i18n.locale)}
							data-flx="companion.records.multi-track.slider"
						/>
					</div>
					<span className={styles.multiTrackTime} data-flx="companion.records.multi-track.time">
						{formatDuration(Math.max(0, positionMs) / 1000, i18n.locale)}
						{' / '}
						{formatDuration(totalDurationMs / 1000, i18n.locale)}
					</span>
				</div>
				{tracks.length > 1 ? (
					<div className={styles.multiTrackTracks} data-flx="companion.records.multi-track.tracks">
						{tracks.map((track) => {
							const isMuted = mutedSpeakers.has(track.speaker);
							return (
								<Button
									key={track.speaker}
									type="button"
									variant={isMuted ? 'secondary' : 'primary'}
									superCompact
									fitContent
									aria-pressed={!isMuted}
									aria-label={i18n._(isMuted ? UNMUTE_TRACK_DESCRIPTOR : MUTE_TRACK_DESCRIPTOR, {
										speaker: track.label,
									})}
									onClick={() => handleToggleMute(track.speaker)}
									data-flx="companion.records.multi-track.button.track-mute"
								>
									{isMuted ? (
										<SpeakerSlashIcon
											size={14}
											aria-hidden="true"
											data-flx="companion.records.multi-track.track-muted-icon"
										/>
									) : (
										<SpeakerHighIcon
											size={14}
											aria-hidden="true"
											data-flx="companion.records.multi-track.track-active-icon"
										/>
									)}
									<span data-flx="companion.records.multi-track.track-label">{track.label}</span>
								</Button>
							);
						})}
					</div>
				) : null}
			</div>
			{error ? (
				<div
					className={`${styles.errorBanner} ${styles.recordingPlaybackError}`}
					role="alert"
					data-flx="companion.records.recording-playback.error"
				>
					<span data-flx="companion.records.multi-track.error-text">{error}</span>
				</div>
			) : null}
		</div>
	);
}
